import { ChatThread, ChatMessage, AgentType } from '../models';

export interface CreateThreadOptions {
  title: string;
  agentType?: AgentType;
  agentThreadId?: string;
  isActive?: boolean;
  messages?: ChatMessage[];
  metadata?: {
    cubeId?: string;
    workflowId?: string;
    context?: unknown;
    messageCount?: number;
  };
}

export class ChatThreadFactory {
  private static generateId(): string {
    return `thread-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public static createThread(options: CreateThreadOptions): ChatThread {
    const now = new Date();
    
    return {
      id: this.generateId(),
      title: options.title,
      messages: options.messages || [],
      agentType: options.agentType,
      agentThreadId: options.agentThreadId,
      createdAt: now,
      updatedAt: now,
      isActive: options.isActive ?? false,
      metadata: options.metadata
    };
  }

  public static createLocalThread(
    threadId: string, 
    agentType: AgentType, 
    title?: string
  ): ChatThread {
    const now = new Date();
    
    return {
      id: threadId,
      title: title || threadId,
      messages: [],
      agentType,
      agentThreadId: threadId,
      createdAt: now,
      updatedAt: now,
      isActive: false,
      metadata: {
        messageCount: 0
      }
    };
  }

  public static fromUserThread(
    userThread: {
      thread_id: string;
      title?: string;
      created_at?: string;
      updated_at?: string;
      message_count?: number;
    },
    agentType?: AgentType,
    messages: ChatMessage[] = []
  ): ChatThread {
    return {
      id: userThread.thread_id,
      title: userThread.title || 'Untitled Thread',
      messages,
      agentType,
      agentThreadId: userThread.thread_id,
      createdAt: userThread.created_at ? new Date(userThread.created_at) : new Date(),
      updatedAt: userThread.updated_at ? new Date(userThread.updated_at) : new Date(),
      isActive: false,
      metadata: {
        messageCount: userThread.message_count || 0
      }
    };
  }

  public static createDefaultThread(agentType: AgentType, title: string): ChatThread {
    return this.createThread({
      title,
      agentType,
      isActive: true
    });
  }

  public static updateThreadMessages(thread: ChatThread, messages: ChatMessage[]): ChatThread {
    return {
      ...thread,
      messages,
      updatedAt: new Date(),
      metadata: {
        ...thread.metadata,
        messageCount: messages.length
      }
    };
  }

  public static markThreadAsActive(thread: ChatThread): ChatThread {
    return {
      ...thread,
      isActive: true,
      updatedAt: new Date()
    };
  }

  public static markThreadAsInactive(thread: ChatThread): ChatThread {
    return {
      ...thread,
      isActive: false
    };
  }
}